import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	registerEmbeddedMacroListeners,
	type EmbeddedForgeOpenModalBridgePayload,
} from '../hooks/useEmbeddedMacroListeners.ts';

import { EmbeddedForgeMacroModal } from './EmbeddedForgeMacroModal.tsx';

export type EmbeddedForgeMacroHostProps = {
	/** The page ADF document — used to discover Connect bodied macros that wrap embedded Forge apps. */
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	document: DocNode | undefined;
	/** Confluence flags container, forwarded to bridge handlers (e.g. `showFlag`). */
	flags: FlagsStateContainer;
};

/**
 * Host that owns the postRobot bridge between embedded Forge apps inside
 * Connect bodied macros and Confluence. It registers all bridge listeners on
 * mount (currently `showFlag`, `openModal`) and renders any product-level UI
 * those listeners produce — today, the embedded Forge macro modal.
 *
 * Intended to be rendered once per page, alongside the renderer that hosts
 * the underlying iframes.
 */
export const EmbeddedForgeMacroHost: FC<EmbeddedForgeMacroHostProps> = ({ document, flags }) => {
	const [openModalPayload, setOpenModalPayload] =
		useState<EmbeddedForgeOpenModalBridgePayload | null>(null);

	const onEmbeddedForgeOpenModal = useCallback((payload: EmbeddedForgeOpenModalBridgePayload) => {
		setOpenModalPayload(payload);
	}, []);

	const dismissOpenModal = useCallback(() => {
		setOpenModalPayload(null);
	}, []);

	useEffect(() => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (!document || !fg('forge_nbm_connect_to_forge_bridge')) {
			return;
		}
		return registerEmbeddedMacroListeners(document, flags, {
			onEmbeddedForgeOpenModal,
		});
	}, [document, flags, onEmbeddedForgeOpenModal]);

	return (
		<EmbeddedForgeMacroModal
			payload={openModalPayload}
			flags={flags}
			onDismiss={dismissOpenModal}
		/>
	);
};
